import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Code, Key, Lock, Copy, CheckCircle, Eye, EyeSlash, ArrowRight } from '@phosphor-icons/react';
import PaymentModal from '../components/PaymentModal';

const endpoints = [
  {
    method: 'GET',
    path: '/api/v1/account',
    description: 'Get account information and balance',
  },
  {
    method: 'GET',
    path: '/api/v1/positions',
    description: 'List all open positions',
  },
  {
    method: 'POST',
    path: '/api/v1/orders',
    description: 'Create a new trading order',
  },
  {
    method: 'GET',
    path: '/api/v1/orders/{id}',
    description: 'Get order status by ID',
  },
  {
    method: 'DELETE',
    path: '/api/v1/orders/{id}',
    description: 'Cancel an order',
  },
  {
    method: 'GET',
    path: '/api/v1/market/prices',
    description: 'Get real-time market prices',
  },
  {
    method: 'GET',
    path: '/api/v1/strategies',
    description: 'List available trading strategies',
  },
  {
    method: 'POST',
    path: '/api/v1/strategies/{id}/execute',
    description: 'Execute a trading strategy',
  },
];

const codeExamples = {
  python: `import requests

API_KEY = "your_api_key_here"
BASE_URL = "https://api.defi-bot.io/v1"

headers = {
    "Authorization": f"Bearer {API_KEY}",
    "Content-Type": "application/json"
}

# Get account info
response = requests.get(
    f"{BASE_URL}/account",
    headers=headers
)
print(response.json())

# Create an order
order = {
    "pair": "ETH/USDT",
    "side": "buy",
    "amount": "1.0",
    "type": "market"
}
response = requests.post(
    f"{BASE_URL}/orders",
    headers=headers,
    json=order
)
print(response.json())`,
  javascript: `const API_KEY = "your_api_key_here";
const BASE_URL = "https://api.defi-bot.io/v1";

// Get account info
const getAccount = async () => {
  const response = await fetch(\`\${BASE_URL}/account\`, {
    headers: {
      "Authorization": \`Bearer \${API_KEY}\`,
      "Content-Type": "application/json"
    }
  });
  return response.json();
};

// Create an order
const createOrder = async (order) => {
  const response = await fetch(\`\${BASE_URL}/orders\`, {
    method: "POST",
    headers: {
      "Authorization": \`Bearer \${API_KEY}\`,
      "Content-Type": "application/json"
    },
    body: JSON.stringify(order)
  });
  return response.json();
};

// Usage
const account = await getAccount();
const order = await createOrder({
  pair: "ETH/USDT",
  side: "buy",
  amount: "1.0",
  type: "market"
});`,
};

const apiPlan = {
  id: 'api',
  name: 'API Access',
  price: 500,
};

const ApiAccess = () => {
  const [activeTab, setActiveTab] = useState('python');
  const [copied, setCopied] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  // Fake API key for demo
  const fakeApiKey = 'dfb_sk_live_xK9m2nP4qR7tV1wY3zA5bC8dE0fG2hI';

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="api-access-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-medium">
            Developer API
          </span>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter text-[var(--text-primary)] mt-4 mb-4">
            Programmatic Access
          </h1>
          <p className="text-lg text-[var(--text-secondary)] max-w-2xl mx-auto">
            Build custom trading solutions with our powerful REST API. Full documentation and SDKs available.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - API Info */}
          <div className="lg:col-span-2 space-y-8">
            {/* API Key Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center">
                  <Key size={20} className="text-[var(--color-primary)]" weight="bold" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[var(--text-primary)]">Your API Key</h3>
                  <p className="text-sm text-[var(--text-secondary)]">Keep this secret and secure</p>
                </div>
              </div>

              <div className="flex items-center gap-2 p-4 rounded-xl bg-[var(--bg-surface-2)] border border-[var(--border-default)]">
                <Lock size={18} className="text-[var(--text-secondary)]" />
                <code className="flex-1 text-sm text-[var(--text-primary)] font-mono">
                  {showApiKey ? fakeApiKey : '••••••••••••••••••••••••••••••••'}
                </code>
                <button
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="p-2 rounded-lg hover:bg-[var(--bg-surface-1)] text-[var(--text-secondary)]"
                  data-testid="toggle-api-key"
                >
                  {showApiKey ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
                <button
                  onClick={() => handleCopy(fakeApiKey)}
                  className="p-2 rounded-lg bg-[var(--color-primary)] text-[var(--bg-primary)]"
                  data-testid="copy-api-key"
                >
                  {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                </button>
              </div>

              <p className="text-xs text-[var(--text-secondary)] mt-3">
                API access requires a Professional or Enterprise plan.{' '}
                <button 
                  onClick={() => setPaymentModalOpen(true)}
                  className="text-[var(--color-primary)] hover:underline"
                >
                  Upgrade now
                </button>
              </p>
            </motion.div>

            {/* Endpoints */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]"
            >
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-4">
                Available Endpoints
              </h3>
              <div className="space-y-3">
                {endpoints.map((endpoint, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-4 p-3 rounded-lg bg-[var(--bg-surface-2)] border border-[var(--border-default)]"
                  >
                    <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${
                      endpoint.method === 'GET' ? 'bg-[var(--accent-success)]/20 text-[var(--accent-success)]' :
                      endpoint.method === 'POST' ? 'bg-[var(--color-primary)]/20 text-[var(--color-primary)]' :
                      'bg-[var(--accent-danger)]/20 text-[var(--accent-danger)]'
                    }`}>
                      {endpoint.method}
                    </span>
                    <code className="text-sm text-[var(--text-primary)] font-mono">
                      {endpoint.path}
                    </code>
                    <span className="ml-auto text-sm text-[var(--text-secondary)] hidden sm:block">
                      {endpoint.description}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Code Examples */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)]"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">
                  Code Examples
                </h3>
                <div className="flex gap-2">
                  {['python', 'javascript'].map((lang) => (
                    <button
                      key={lang}
                      onClick={() => setActiveTab(lang)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        activeTab === lang
                          ? 'bg-[var(--color-primary)] text-[var(--bg-primary)]'
                          : 'bg-[var(--bg-surface-2)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                      }`}
                      data-testid={`tab-${lang}`}
                    >
                      {lang.charAt(0).toUpperCase() + lang.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative">
                <pre className="p-4 rounded-xl bg-[#0D0F14] border border-[var(--border-default)] overflow-x-auto">
                  <code className="text-sm text-[var(--text-primary)] font-mono whitespace-pre">
                    {codeExamples[activeTab]}
                  </code>
                </pre>
                <button
                  onClick={() => handleCopy(codeExamples[activeTab])}
                  className="absolute top-4 right-4 p-2 rounded-lg bg-[var(--bg-surface-2)] text-[var(--text-secondary)] hover:text-[var(--color-primary)]"
                  data-testid="copy-code"
                >
                  {copied ? <CheckCircle size={18} /> : <Copy size={18} />}
                </button>
              </div>
            </motion.div>
          </div>

          {/* Right Column - Purchase Card */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="sticky top-24 p-6 rounded-2xl bg-gradient-to-b from-[var(--color-primary)]/10 to-[var(--bg-surface-1)] border border-[var(--color-primary)]/30"
            >
              <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/20 flex items-center justify-center mb-4">
                <Code size={24} className="text-[var(--color-primary)]" weight="bold" />
              </div>

              <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">
                API Access
              </h3>
              <p className="text-sm text-[var(--text-secondary)] mb-6">
                Get full API access to integrate DeFi Bot into your applications.
              </p>

              <div className="mb-6">
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-[var(--text-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>
                    $500
                  </span>
                  <span className="text-[var(--text-secondary)]">USDT/USDC</span>
                </div>
                <p className="text-xs text-[var(--text-secondary)] mt-1">
                  Included with Professional plan
                </p>
              </div>

              <ul className="space-y-3 mb-6">
                {['100K API calls/month', 'WebSocket support', 'Rate limit: 100 req/min', 'Full documentation', 'SDKs (Python, JS, Go)'].map((feature) => (
                  <li key={feature} className="flex items-center gap-2 text-sm text-[var(--text-primary)]">
                    <CheckCircle size={16} className="text-[var(--accent-success)]" weight="bold" />
                    {feature}
                  </li>
                ))}
              </ul>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setPaymentModalOpen(true)}
                className="w-full flex items-center justify-center gap-2 py-4 bg-[var(--color-primary)] text-[var(--bg-primary)] font-semibold rounded-xl"
                data-testid="purchase-api-btn"
              >
                Purchase API Access
                <ArrowRight size={20} />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </div>

      <PaymentModal
        isOpen={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        plan={apiPlan}
      />
    </div>
  );
};

export default ApiAccess;
